import { useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useMutationSetEmailNotificationStatus } from 'api/mutations/setEmailNotificationStatus.mutation';
import { useMutationSetNotificationsReadStatus } from 'api/mutations/setNotificationsReadStatus.mutation';
import { useQueryGetEmailNotificationStatus } from 'api/queries/getEmailNotificationStatus.query';
import { useQueryGetNotifications } from 'api/queries/getNotifications.query';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';

import NotificationGroup from 'components/App/AuthenticateLayout/Header/Notification/NotificationGroup/NotificationGroup';
import classes from 'components/App/AuthenticateLayout/Header/Notification/NotificationList/NotificationList.module.scss';
import { IGetNotificationsResponse } from 'components/App/AuthenticateLayout/Header/Notification/Notifications.types';
import { groupNotificationsByDate } from 'components/App/AuthenticateLayout/Header/Notification/Notifications.utils';
import { Switch } from 'components/DesignSystem/Switch';

interface INotificationListProps {
    show: boolean;
}

const NotificationList = ({ show }: INotificationListProps) => {
    const [showUnread, setShowUnread] = useState(false);
    const { isLoading, data: notifications } = useQueryGetNotifications();
    const queryClient = useQueryClient();
    const mutationReadStatus = useMutationSetNotificationsReadStatus();
    const { data: emailNotificationStatus, refetch } = useQueryGetEmailNotificationStatus();
    const mutationEmailNotificationStatus = useMutationSetEmailNotificationStatus();
    const { email } = useUserHook();
    const { handleSnackBar } = useCustomSnackBar();

    const handleShowUnread = () => {
        setShowUnread(!showUnread);
    };

    const setAllNotificationsAsRead = () => {
        mutationReadStatus.mutate(
            {
                consumer_email: email,
                id_list: [],
                read: true,
            },
            {
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const handleEmailNotificationStatus = () => {
        mutationEmailNotificationStatus.mutate(
            {
                send_notification: !emailNotificationStatus,
            },
            {
                onSuccess: () => {
                    return refetch();
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    useEffect(() => {
        show && queryClient.invalidateQueries('useQueryGetNotifications');
    }, [show]);

    const filteredList = showUnread ? notifications?.filter((item) => !item.isRead) : notifications;

    const grouped = filteredList ? groupNotificationsByDate<IGetNotificationsResponse>({ list: filteredList }) : [];

    return (
        <div className={`${classes.notificationList} ${show && classes.show}`}>
            {isLoading && <OverlayWithSpinner />}
            <div className={classes.header}>
                <div className={classes.title}>Notifications</div>
                <div className={classes.configuration}>
                    <div className={classes.item}>
                        <Switch
                            id={'email-notifications'}
                            checked={emailNotificationStatus}
                            onChange={handleEmailNotificationStatus}
                            label={'Email notifications'}
                            labelPlacement={'end'}
                        />
                    </div>
                    <div className={classes.item}>
                        <Switch
                            id={'show-unread'}
                            checked={showUnread}
                            onChange={handleShowUnread}
                            label={'Only show unread '}
                            labelPlacement={'end'}
                        />
                    </div>
                    <div className={`${classes.item} ${classes.clickable}`} onClick={setAllNotificationsAsRead}>
                        Mark all as read
                    </div>
                </div>
            </div>

            <div className={classes.list}>
                {grouped.length === 0 && (
                    <div className={classes.emptyList}>
                        <MuiIcon icon={'message'} fontSize={'9rem'} color={'#9c9c9c'} />
                        No notifications yet...
                    </div>
                )}
                {grouped.map((group) => (
                    <NotificationGroup key={group.title} group={group} />
                ))}
            </div>
        </div>
    );
};

export default NotificationList;
